import { hourOf } from "./clock.js";
import { SEED, seedSrc } from "./seed.js";
import type { DemoState } from "./types.js";

export interface ViewPhoto {
  readonly id: string;
  readonly large: string;
  readonly small: string;
  readonly author: string;
  readonly at: number;
  readonly mine: boolean;
  readonly width: number;
  readonly height: number;
}

export interface HourGroup {
  readonly hour: number;
  readonly photos: readonly ViewPhoto[];
}

export function allPhotos(state: DemoState, urls: ReadonlyMap<string, string>): ViewPhoto[] {
  const seeded = SEED.map((seed) => ({
    id: seed.id,
    large: seedSrc(seed.id, "l"),
    small: seedSrc(seed.id, "s"),
    author: seed.author,
    at: seed.at,
    mine: false,
    width: seed.width,
    height: seed.height,
  }));
  const mine = state.photos.flatMap((photo) => {
    const url = urls.get(photo.id);
    return url
      ? [
          {
            id: photo.id,
            large: url,
            small: url,
            author: photo.author,
            at: photo.at,
            mine: true,
            width: photo.width,
            height: photo.height,
          },
        ]
      : [];
  });
  return [...seeded, ...mine].sort((first, second) => first.at - second.at);
}

export function visiblePhotos(
  photos: readonly ViewPhoto[],
  hidden: readonly string[],
): ViewPhoto[] {
  return photos.filter((photo) => !hidden.includes(photo.id));
}

export function newestFirst(photos: readonly ViewPhoto[]): ViewPhoto[] {
  return [...photos].sort((first, second) => second.at - first.at);
}

export function byHour(photos: readonly ViewPhoto[]): HourGroup[] {
  const groups = new Map<number, ViewPhoto[]>();
  for (const photo of newestFirst(photos)) {
    const slot = Math.floor(photo.at / 60);
    groups.set(slot, [...(groups.get(slot) ?? []), photo]);
  }
  return [...groups.entries()].map(([slot, list]) => ({ hour: hourOf(slot * 60), photos: list }));
}

export function shortName(full: string): string {
  const [first = "", ...rest] = full.trim().split(/\s+/);
  const last = rest.at(-1);
  return last ? `${first} ${last.charAt(0)}.` : first;
}
